/**
 * Settings: funds, locations, categories, currency and rates, password.
 */
import { useState } from 'react';
import { Archive, ArchiveRestore, Download, Loader2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge, Label } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { PageHeader } from '@/components/common';
import { COLOR_TOKENS, colorClasses } from '@/lib/colors';
import { formatDate, formatRate, todayIso } from '@/lib/format';
import {
  useArchiveFund,
  useArchiveLocation,
  useCategories,
  useChangePassword,
  useCurrencies,
  useFetchFxRates,
  useFunds,
  useFxRates,
  useLocations,
  useSaveCategory,
  useSaveFund,
  useSaveLocation,
  useSaveSettings,
  useSetManualRate,
  useSettings,
} from '@/hooks/useApi';
import type { Category, Fund, Location } from '@/lib/types';

export function SettingsPage(): JSX.Element {
  return (
    <div>
      <PageHeader title="Настройки" />

      <Tabs defaultValue="funds">
        <TabsList className="w-full overflow-x-auto sm:w-auto">
          <TabsTrigger value="funds">Каси</TabsTrigger>
          <TabsTrigger value="locations">Места</TabsTrigger>
          <TabsTrigger value="categories">Категории</TabsTrigger>
          <TabsTrigger value="currency">Валути</TabsTrigger>
          <TabsTrigger value="account">Профил</TabsTrigger>
        </TabsList>

        <TabsContent value="funds">
          <FundsTab />
        </TabsContent>
        <TabsContent value="locations">
          <LocationsTab />
        </TabsContent>
        <TabsContent value="categories">
          <CategoriesTab />
        </TabsContent>
        <TabsContent value="currency">
          <CurrencyTab />
        </TabsContent>
        <TabsContent value="account">
          <AccountTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────────────── funds ─────────────────────────────

function FundsTab(): JSX.Element {
  const { data: funds = [] } = useFunds(true);
  const [editing, setEditing] = useState<Partial<Fund> | null>(null);
  const save = useSaveFund();
  const archive = useArchiveFund();

  const handleArchive = async (fund: Fund) => {
    try {
      await archive.mutateAsync({ id: fund.id, archived: !fund.archived });
      toast.success(fund.archived ? 'Касата е възстановена' : 'Касата е архивирана');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Каси</CardTitle>
          <CardDescription>За какво са парите.</CardDescription>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setEditing({ color: 'slate' })}>
          <Plus className="h-4 w-4" />
          Нова
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {funds.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-md border p-3">
            <span className={cn('h-3 w-3 shrink-0 rounded-full', colorClasses(f.color).bg)} />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 truncate font-medium">
                {f.name}
                {f.archived ? (
                  <Badge variant="neutral" className="text-[10px]">
                    архивирана
                  </Badge>
                ) : null}
              </p>
              {f.note ? <p className="truncate text-xs text-muted-foreground">{f.note}</p> : null}
            </div>
            <Button variant="ghost" size="icon-sm" onClick={() => setEditing(f)} aria-label="Редактирай">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void handleArchive(f)}
              aria-label={f.archived ? 'Възстанови' : 'Архивирай'}
            >
              {f.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
          </div>
        ))}
      </CardContent>

      <ResponsiveDialog
        open={editing != null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Промяна на каса' : 'Нова каса'}
      >
        {editing ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void (async () => {
                try {
                  await save.mutateAsync({
                    id: editing.id,
                    name: editing.name,
                    color: editing.color ?? 'slate',
                    note: editing.note || undefined,
                  });
                  toast.success('Записано');
                  setEditing(null);
                } catch (err) {
                  toast.error((err as Error).message);
                }
              })();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="f-name">Име</Label>
              <Input
                id="f-name"
                value={editing.name ?? ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-note">Бележка</Label>
              <Input
                id="f-note"
                value={editing.note ?? ''}
                onChange={(e) => setEditing({ ...editing, note: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Цвят</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_TOKENS.map((token) => (
                  <button
                    key={token}
                    type="button"
                    aria-label={token}
                    onClick={() => setEditing({ ...editing, color: token })}
                    className={cn(
                      'h-7 w-7 rounded-full ring-offset-2 ring-offset-background transition-all',
                      colorClasses(token).bg,
                      editing.color === token && 'ring-2 ring-foreground',
                    )}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditing(null)}>
                Откажи
              </Button>
              <Button type="submit" className="flex-1" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Запиши
              </Button>
            </div>
          </form>
        ) : null}
      </ResponsiveDialog>
    </Card>
  );
}

// ─────────────────────────── locations ───────────────────────────

const LOCATION_KIND_LABELS = {
  BANK: 'Банка',
  CASH: 'Кеш',
  CARD: 'Карта',
  OTHER: 'Друго',
} as const;

function LocationsTab(): JSX.Element {
  const { data: locations = [] } = useLocations(true);
  const [editing, setEditing] = useState<Partial<Location> | null>(null);
  const save = useSaveLocation();
  const archive = useArchiveLocation();

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Места</CardTitle>
          <CardDescription>Къде физически стоят парите.</CardDescription>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setEditing({ kind: 'CASH' })}>
          <Plus className="h-4 w-4" />
          Ново
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {locations.map((l) => (
          <div key={l.id} className="flex items-center gap-3 rounded-md border p-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 truncate font-medium">
                {l.name}
                <Badge variant="neutral" className="text-[10px]">
                  {LOCATION_KIND_LABELS[l.kind]}
                </Badge>
                {l.archived ? (
                  <Badge variant="neutral" className="text-[10px]">
                    архивирано
                  </Badge>
                ) : null}
              </p>
              {l.holderName ? (
                <p className="truncate text-xs text-muted-foreground">Държи го: {l.holderName}</p>
              ) : null}
            </div>
            <Button variant="ghost" size="icon-sm" onClick={() => setEditing(l)} aria-label="Редактирай">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={l.archived ? 'Възстанови' : 'Архивирай'}
              onClick={() => {
                void (async () => {
                  try {
                    await archive.mutateAsync({ id: l.id, archived: !l.archived });
                    toast.success(l.archived ? 'Възстановено' : 'Архивирано');
                  } catch (e) {
                    toast.error((e as Error).message);
                  }
                })();
              }}
            >
              {l.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
          </div>
        ))}
      </CardContent>

      <ResponsiveDialog
        open={editing != null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Промяна на място' : 'Ново място'}
      >
        {editing ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void (async () => {
                try {
                  await save.mutateAsync({
                    id: editing.id,
                    name: editing.name,
                    kind: editing.kind ?? 'CASH',
                    holderName: editing.holderName || undefined,
                  });
                  toast.success('Записано');
                  setEditing(null);
                } catch (err) {
                  toast.error((err as Error).message);
                }
              })();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="l-name">Име</Label>
              <Input
                id="l-name"
                value={editing.name ?? ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Вид</Label>
              <Select
                value={editing.kind ?? 'CASH'}
                onValueChange={(v) => setEditing({ ...editing, kind: v as Location['kind'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LOCATION_KIND_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="l-holder">Държи го (ако не си ти)</Label>
              <Input
                id="l-holder"
                value={editing.holderName ?? ''}
                onChange={(e) => setEditing({ ...editing, holderName: e.target.value })}
                placeholder="напр. Иван, касиер на входа"
              />
              <p className="text-xs text-muted-foreground">
                Попълни го, ако парите са при друг човек — тогава се показват отделно на началния
                екран.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditing(null)}>
                Откажи
              </Button>
              <Button type="submit" className="flex-1" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Запиши
              </Button>
            </div>
          </form>
        ) : null}
      </ResponsiveDialog>
    </Card>
  );
}

// ─────────────────────────── categories ───────────────────────────

function CategoriesTab(): JSX.Element {
  const { data: categories = [] } = useCategories();
  const { data: funds = [] } = useFunds();
  const [editing, setEditing] = useState<Partial<Category> | null>(null);
  const save = useSaveCategory();

  const income = categories.filter((c) => c.kind === 'INCOME');
  const expense = categories.filter((c) => c.kind === 'EXPENSE');

  const renderGroup = (title: string, list: Category[]) => (
    <div>
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {list.map((c) => {
          const colors = colorClasses(c.color);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setEditing(c)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm transition-colors hover:bg-accent',
                colors.soft,
                colors.text,
                colors.border,
              )}
            >
              {c.name}
              {c.fundId ? (
                <span className="ml-1 opacity-60">
                  · {funds.find((f) => f.id === c.fundId)?.name ?? '—'}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Категории</CardTitle>
          <CardDescription>
            Без каса означава, че важи за всички. Категориите се подреждат по честота при въвеждане.
          </CardDescription>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setEditing({ kind: 'EXPENSE', color: 'slate' })}>
          <Plus className="h-4 w-4" />
          Нова
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {renderGroup('Разходи', expense)}
        {renderGroup('Приходи', income)}
      </CardContent>

      <ResponsiveDialog
        open={editing != null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Промяна на категория' : 'Нова категория'}
      >
        {editing ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void (async () => {
                try {
                  await save.mutateAsync({
                    id: editing.id,
                    name: editing.name,
                    kind: editing.kind,
                    fundId: editing.fundId ?? null,
                    color: editing.color ?? 'slate',
                  });
                  toast.success('Записано');
                  setEditing(null);
                } catch (err) {
                  toast.error((err as Error).message);
                }
              })();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Име</Label>
              <Input
                id="c-name"
                value={editing.name ?? ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label>Вид</Label>
              <Select
                value={editing.kind ?? 'EXPENSE'}
                onValueChange={(v) => setEditing({ ...editing, kind: v as Category['kind'] })}
                disabled={editing.id != null}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EXPENSE">Разход</SelectItem>
                  <SelectItem value="INCOME">Приход</SelectItem>
                </SelectContent>
              </Select>
              {editing.id ? (
                <p className="text-xs text-muted-foreground">
                  Видът не се променя след създаване — това би преобърнало всички минали справки.
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Каса</Label>
              <Select
                value={editing.fundId ? String(editing.fundId) : 'all'}
                onValueChange={(v) =>
                  setEditing({ ...editing, fundId: v === 'all' ? null : Number(v) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Всички каси</SelectItem>
                  {funds.map((f) => (
                    <SelectItem key={f.id} value={String(f.id)}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Цвят</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_TOKENS.map((token) => (
                  <button
                    key={token}
                    type="button"
                    aria-label={token}
                    onClick={() => setEditing({ ...editing, color: token })}
                    className={cn(
                      'h-7 w-7 rounded-full ring-offset-2 ring-offset-background transition-all',
                      colorClasses(token).bg,
                      editing.color === token && 'ring-2 ring-foreground',
                    )}
                  />
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditing(null)}>
                Откажи
              </Button>
              <Button type="submit" className="flex-1" disabled={save.isPending}>
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Запиши
              </Button>
            </div>
          </form>
        ) : null}
      </ResponsiveDialog>
    </Card>
  );
}

// ─────────────────────────── currency ───────────────────────────

function CurrencyTab(): JSX.Element {
  const { data: settings } = useSettings();
  const { data: currencies = [] } = useCurrencies();
  const { data: fx } = useFxRates();
  const saveSettings = useSaveSettings();
  const fetchRates = useFetchFxRates();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Базова валута</CardTitle>
          <CardDescription>
            В нея се показват всички обобщения. Наличностите се пазят в оригиналната си валута.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={settings?.baseCurrency ?? 'EUR'}
            onValueChange={(v) => {
              void (async () => {
                try {
                  const res = await saveSettings.mutateAsync({ baseCurrency: v });
                  if (res.mixedHistory && res.mixedHistory > 0) {
                    toast.warning(
                      `Базовата валута е сменена. ${res.mixedHistory} стари записа остават с историческа стойност в предишната валута — това е нарочно, за да не се пренапише историята.`,
                      { duration: 12_000 },
                    );
                  } else {
                    toast.success('Базовата валута е сменена');
                  }
                } catch (e) {
                  toast.error((e as Error).message);
                }
              })();
            }}
          >
            <SelectTrigger className="sm:max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.symbol} {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Смяната не пренаписва историческите стойности на минали операции — те остават в
            валутата, която е била базова тогава.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Валутни курсове</CardTitle>
            <CardDescription>
              Дневно от ЕЦБ. BGN е фиксиран на 1,95583 и не се тегли.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={fetchRates.isPending}
            onClick={() => {
              void (async () => {
                try {
                  const res = await fetchRates.mutateAsync(false);
                  toast.success(
                    res.updated.length > 0
                      ? `Обновени: ${res.updated.join(', ')}`
                      : 'Няма нови курсове',
                  );
                } catch (e) {
                  toast.error((e as Error).message);
                }
              })();
            }}
          >
            {fetchRates.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Обнови
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(fx?.rates ?? []).map((r) => (
              <div key={r.quote} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2">
                  <span className="font-medium">
                    {fx?.base}/{r.quote}
                  </span>
                  <Badge variant="neutral" className="text-[10px]">
                    {r.source === 'PEG' ? 'фиксиран' : r.source === 'MANUAL' ? 'ръчен' : 'ЕЦБ'}
                  </Badge>
                </span>
                <span className="flex items-center gap-3">
                  <span className="tabular">{formatRate(r.rate)}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(r.date)}</span>
                </span>
              </div>
            ))}
            {(fx?.rates ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Още няма изтеглени курсове. Натисни „Обнови".
              </p>
            ) : null}
          </div>

          <ManualRateForm />
        </CardContent>
      </Card>
    </div>
  );
}

function ManualRateForm(): JSX.Element {
  const { data: currencies = [] } = useCurrencies();
  const [quote, setQuote] = useState('');
  const [date, setDate] = useState(todayIso());
  const [rate, setRate] = useState('');
  const setManual = useSetManualRate();

  return (
    <form
      className="mt-5 space-y-3 border-t pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void (async () => {
          try {
            await setManual.mutateAsync({ quote, date, rate });
            toast.success('Курсът е записан');
            setRate('');
          } catch (err) {
            toast.error((err as Error).message);
          }
        })();
      }}
    >
      <p className="text-sm font-medium">Ръчен курс</p>
      <p className="text-xs text-muted-foreground">
        Колко единици от валутата се получават за 1 EUR — както в подаването на ЕЦБ.
      </p>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <Select value={quote} onValueChange={setQuote}>
          <SelectTrigger aria-label="Валута">
            <SelectValue placeholder="Валута" />
          </SelectTrigger>
          <SelectContent>
            {currencies
              .filter((c) => c.code !== 'EUR')
              .map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Дата" />
        <Input
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          inputMode="decimal"
          placeholder="напр. 0.8412"
          aria-label="Курс"
        />
        <Button type="submit" disabled={!quote || !rate || setManual.isPending}>
          {setManual.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Запиши
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────── account ───────────────────────────

function AccountTab(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const changePassword = useChangePassword();

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Смяна на паролата</CardTitle>
        <CardDescription>Всички други сесии ще бъдат прекратени.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="max-w-sm space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void (async () => {
              try {
                await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
                toast.success('Паролата е сменена. Влез отново.');
                setCurrent('');
                setNext('');
                setConfirm('');
                // The server revoked the refresh cookie; a reload lands on login.
                setTimeout(() => window.location.reload(), 1200);
              } catch (err) {
                toast.error((err as Error).message);
              }
            })();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="p-current">Текуща парола</Label>
            <Input
              id="p-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-new">Нова парола</Label>
            <Input
              id="p-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
            {tooShort ? (
              <p className="text-xs text-destructive">Минимум 10 знака.</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-confirm">Потвърди</Label>
            <Input
              id="p-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
            {mismatch ? <p className="text-xs text-destructive">Паролите не съвпадат.</p> : null}
          </div>
          <Button
            type="submit"
            disabled={!current || !next || mismatch || tooShort || changePassword.isPending}
          >
            {changePassword.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Смени паролата
          </Button>
        </form>

        <div className="mt-6 border-t pt-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Download className="h-4 w-4" />
            Резервно копие
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Базата се архивира всяка нощ на сървъра чрез <code>scripts/nightly-backup.sh</code>.
            Възстановяване: <code>scripts/restore.sh</code>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
